import {
  type Cell,
  type ColumnDef,
  type Row,
  type Table,
} from "@tanstack/react-table";

import {
  TableBody,
  TableCell,
  TableRow,
} from "@vega-core/components/shadcn/table";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import {
  EmptyCard,
  type EmptyCardProps,
} from "../../../Cards/EmptyCard/EmptyCard";
import { RowsLoading } from "../../RowsLoading";
import { type ExpandableRowConfig } from "../../types";

import { TableCellComponent } from "./TableCell";
import { TableRowComponent } from "./TableRow";

type TableBodyProps<TData, TValue> = {
  table: Table<TData>;
  data: TData[];
  columns: ColumnDef<TData, TValue>[];
  isLoading?: boolean;
  emptyState?: EmptyCardProps;
  rowClassName?: string;
  cellClassName?: string;
  collapsible?: boolean;
  collapsed: boolean;
  onRowClick?: (row: TData, rowId: string) => void;
  hasRowClick?: boolean;
  enableRowSelection?: boolean;
  selectOnRowClick?: boolean;
  getCanSelect?: (row: TData) => boolean;
  pinnedLeftColumns?: string[];
  isScrolled?: boolean;
  getRowClassName?: (row: TData) => string;
  expandableConfig?: ExpandableRowConfig<TData>;
  rows?: Row<TData>[];
};

export const TableBodyComponent = <TData, TValue>({
  table,
  data,
  columns,
  isLoading,
  emptyState,
  rowClassName,
  cellClassName,
  collapsible,
  collapsed,
  onRowClick,
  hasRowClick,
  enableRowSelection = false,
  selectOnRowClick = false,
  getCanSelect,
  pinnedLeftColumns = [],
  isScrolled = false,
  getRowClassName,
  expandableConfig,
  rows,
}: TableBodyProps<TData, TValue>) => {
  // this directive is needed to prevent memoization issues caused by incompatibility between React compiler and TanStack Table https://github.com/TanStack/table/issues/5567
  "use no memo";
  const tableRows: Row<TData>[] = rows ?? table.getRowModel().rows;

  // For loading state
  if (isLoading) {
    return <RowsLoading columnsNumber={table.getAllColumns().length} />;
  }

  // For empty state
  if (data.length === 0 && emptyState) {
    return (
      <TableBody>
        <TableRow className="hover:bg-transparent p-0">
          <TableCell
            className="align-middle p-0 first:pl-0 last:pr-0 w-full bg-background/card"
            colSpan={table.getAllColumns().length}
          >
            <div className="flex items-center justify-center lg:pb-12">
              <EmptyCard
                {...emptyState}
                className="border-0 rounded-none shadow-none! min-h-0 h-auto py-4 border-t border-border/secondary"
              />
            </div>
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  return (
    <TableBody
      className={cnTwMerge("w-full", {
        hidden: isTruthy(collapsible) && collapsed,
      })}
    >
      {tableRows.map((row) => {
        const rowClassnameByRow = getRowClassName?.(row.original);
        const cellClassnameByRow = columns.map((column) =>
          column.meta?.getCellClassNameByRow?.(row.original),
        );

        let expandableRowClassName = "";

        if (
          expandableConfig?.enableChildRowStyling &&
          !expandableConfig.disableStyling
        ) {
          const rowLevel = expandableConfig.getRowLevel(row.original);
          const parentId = expandableConfig.getParentId?.(row.original);

          if (rowLevel > 0 && parentId) {
            const isChildOfExpanded = tableRows.some(
              (tableRow) =>
                expandableConfig.getRowId(tableRow.original) === parentId,
            );

            if (isChildOfExpanded) {
              expandableRowClassName =
                expandableConfig.getChildRowClassName?.(
                  row.original,
                  rowLevel,
                ) || "bg-background/secondary";
            }
          }
        }

        const handleRowClick = () => {
          if (selectOnRowClick && enableRowSelection) {
            const canSelect = getCanSelect ? getCanSelect(row.original) : true;

            if (canSelect) {
              row.toggleSelected();
            }
          }

          if (onRowClick) {
            onRowClick(row.original, row.id);
          }
        };

        const cells = row
          .getVisibleCells()
          .map((cell, index) => (
            <TableCellComponent
              cell={cell as Cell<TData, TValue>}
              cellByRowClassName={cellClassnameByRow[index]}
              cellClassName={cellClassName}
              dataTestId={`cell-${row.id}-${cell.column.id}`}
              isScrolled={isScrolled}
              key={cell.id}
              pinnedLeftColumns={pinnedLeftColumns}
              rowClassName={rowClassnameByRow}
            />
          ));

        return (
          <TableRowComponent
            cells={cells}
            dataTestId={`row-${row.id}`}
            hasRowClick={hasRowClick}
            isSelected={row.getIsSelected()}
            key={row.id}
            onClick={handleRowClick}
            row={row}
            rowClassName={rowClassName}
            rowClassnameByRow={cnTwMerge(
              rowClassnameByRow,
              expandableRowClassName,
            )}
          />
        );
      })}
    </TableBody>
  );
};
